"use client";

import { type ReactNode } from "react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { filterSegmentTriggerClassName } from "./shared";
import { Icon } from "@/components/ui/icon";

// Minimal shape the component itself needs (value + label). The generic
// `T extends SegmentOption` then infers the caller's concrete option type, so
// `dropdownContent(opt)` sees the real fields (e.g. badgeVariant) instead of
// `unknown`. (An earlier `[key: string]: unknown` index signature here forced
// every caller's option type to declare that signature too, which broke
// strongly-typed callers like the filter `select`.)
interface SegmentOption {
  value: string;
  label: string;
}

export function SegmentSelect<T extends SegmentOption>({
  value,
  options,
  onChange,
  children,
  className,
  dropdownContent,
  maxDropdownHeight,
}: {
  value: string;
  options: T[];
  onChange: (v: string) => void;
  children: ReactNode;
  className?: string;
  dropdownContent?: (opt: T, isSelected: boolean) => ReactNode;
  maxDropdownHeight?: number;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            filterSegmentTriggerClassName,
            "max-w-[180px]",
            className,
          )}
        >
          <span className="flex min-w-0 items-center truncate">{children}</span>
          <Icon
            name="IconChevronGrabberVertical"
            size={10}
            className="text-heading opacity-20 shrink-0"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        style={
          maxDropdownHeight
            ? { maxHeight: maxDropdownHeight, overflowY: "auto" }
            : undefined
        }
      >
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onSelect={() => onChange(opt.value)}
            className={opt.value === value ? "bg-contrast/6" : ""}
          >
            {dropdownContent
              ? dropdownContent(opt, opt.value === value)
              : opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
